// --- Authenticate.jsx (Enhanced) ---

import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { Spinner } from 'react-bootstrap';
import quoteyBot from '../logos/common/quotey-homepage-bot.svg';
import * as theme from '../theme';
import translations from '../utils/translations';
import { getUserLanguagePreference } from '../store/userManagement';
import {
  loginBrokerWithSSO,
  verifyToken,
  getUserRoles,
  getProvinces,
  getLoginAttemptSuccess,
} from '../store/broker';

export const Authenticate = () => {
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginSuccess = useSelector(getLoginAttemptSuccess);
  const languagePreference = useSelector(getUserLanguagePreference) || 'en';

  // Loading state management
  const [authStep, setAuthStep] = useState('authenticating');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // This function will be called after a successful Stytch session is established,
  // regardless of the login method (Magic Link, SSO, etc.).
  const handleSuccessfulStytchSession = useCallback(async () => {
    try {
      setAuthStep('verifyingCredentials');
      const { session: sdkSession } = await stytch.session.getInfo();

      if (!sdkSession) {
        throw new Error('Session not established after authentication.');
      }

      setAuthStep('settingUpSession');

      // The session JWT is Stytch's own signed statement of who this member is, and it
      // is the only thing the backend accepts. Reading an email out of the session and
      // posting it ourselves would just be us asserting an identity, which the backend
      // has no way to check -- so we hand over the token and let it ask Stytch directly.
      //
      // This also retires the per-method email plumbing that used to live here. SSO put
      // the address in a saml_sso_factor, magic links in an email_factor, and
      // impersonation in neither -- which is why this function used to take a
      // knownEmail argument. The backend now reads member.email_address off its own
      // sessions.authenticate() response, which is populated identically for all three.
      const tokens = stytch.session.getTokens();

      if (!tokens?.session_jwt) {
        console.error('No session JWT available after authentication.');
        throw new Error('No session token available after authentication.');
      }

      setAuthStep('loadingWorkspace');

      // Dispatch the action to log the user into your backend system
      dispatch(loginBrokerWithSSO({ session_jwt: tokens.session_jwt }));

    } catch (err) {
      console.error('[Auth] Error handling Stytch session:', err);
      setHasError(true);
      setErrorMessage(err.message || 'Authentication failed');
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [stytch, dispatch, navigate]);


  useEffect(() => {
    const authenticate = async () => {
      const params = new URLSearchParams(window.location.search);
      const tokenType = params.get('stytch_token_type');
      const token = params.get('token');

      // If there's no token, we can't do anything. Redirect to login.
      if (!token || !tokenType) {
        return navigate('/login');
      }

      try {
        setAuthStep('authenticating');
        setIsLoading(true);

        // Use a switch to handle different kinds of tokens from Stytch
        switch (tokenType) {
          case 'discovery':
            const {
              intermediate_session_token: ist,
              discovered_organizations: orgs,
            } = await stytch.magicLinks.discovery.authenticate({
              discovery_magic_links_token: token,
            });

            if (!orgs.length) {
              throw new Error('No organizations discovered for this user.');
            }

            // For simplicity, we'll log into the first organization found.
            const orgId = orgs[0].organization.organization_id;

            // Exchange the intermediate token for a full session token
            await stytch.discovery.intermediateSessions.exchange({
              intermediate_session_token: ist,
              organization_id: orgId,
              session_duration_minutes: 60,
            });
            // The exchange call establishes the session. Now handle it.
            await handleSuccessfulStytchSession();
            break;

          case 'sso':
            // An SSO token can be authenticated directly to create a session.
            await stytch.sso.authenticate({
              sso_token: token,
              session_duration_minutes: 60,
            });
            // The authenticate call establishes the session. Now handle it.
            await handleSuccessfulStytchSession();
            break;

          // It's good practice to also handle 'oauth' for social logins like Google,
          // as Stytch can use this under the hood for some SSO configurations.
          case 'oauth':
            await stytch.oauth.authenticate({
              oauth_token: token,
              session_duration_minutes: 60,
            });
            await handleSuccessfulStytchSession();
            break;

          case 'multi_tenant_impersonation': {
            // A support-manager-initiated impersonation session (Stytch
            // Dashboard > Password and user policies > "Allow user
            // impersonation"). No session_duration_minutes param here --
            // impersonation sessions aren't extendable the way login
            // sessions are.
            await stytch.impersonation.authenticate({
              impersonation_token: token,
            });
            // The call above establishes a session for the impersonated member, so its
            // JWT already identifies them -- no need to carry the email over separately
            // the way this branch used to. The backend resolves the same member from
            // that token.
            await handleSuccessfulStytchSession();
            break;
          }

          default:
            // If we see a token type we don't recognize, it's an error.
            console.error(`Unrecognized stytch_token_type: ${tokenType}`);
            setHasError(true);
            setErrorMessage(`Unrecognized token type: ${tokenType}`);
            setTimeout(() => navigate('/login'), 3000);
            return;
        }

        // Clean the URL to prevent the token from being used again on refresh.
        window.history.replaceState({}, document.title, window.location.pathname);

      } catch (err) {
        console.error('[Auth] Authentication failed:', err);
        setHasError(true);
        setErrorMessage(err.message || 'Authentication failed');
        setIsLoading(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    authenticate();
    // We only want this effect to run once when the component mounts.
    // The functions from useCallback have stable identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stytch, navigate, handleSuccessfulStytchSession]);


  useEffect(() => {
    // This effect handles the result of your backend login attempt
    if (loginSuccess === true) {
      setAuthStep('welcomeBack');
      setTimeout(() => {
        dispatch(verifyToken());
        dispatch(getUserRoles());
        dispatch(getProvinces());
        navigate('/');
      }, 1500);
    } else if (loginSuccess === false) {
      // The backend login failed (e.g., user not found in your DB)
      setHasError(true);
      setErrorMessage('Login failed. Please try again.');
      setIsLoading(false);
      setTimeout(() => navigate('/login'), 3000);
    }
    // This effect runs ONLY when loginSuccess changes.
  }, [loginSuccess, dispatch, navigate]);

  // Get current auth message based on step and language
  const getAuthMessage = () => {
    const messages = translations[languagePreference]?.authentication || translations.en.authentication;
    switch (authStep) {
      case 'authenticating':
        return messages.authenticating;
      case 'verifyingCredentials':
        return messages.verifyingCredentials;
      case 'settingUpSession':
        return messages.settingUpSession;
      case 'loadingWorkspace':
        return messages.loadingWorkspace;
      case 'welcomeBack':
        return messages.welcomeBack;
      default:
        return messages.authenticating;
    }
  };

  const getSubMessage = () => {
    const messages = translations[languagePreference]?.authentication || translations.en.authentication;
    if (hasError) {
      return errorMessage;
    }
    if (authStep === 'welcomeBack') {
      return messages.almostDone;
    }
    return messages.pleaseWait;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.COLORS.tertiary_lightbackground} 0%, ${theme.COLORS.tertiary_ligthindigo} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'GibsonRegular, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '3rem 2rem',
        boxShadow: '0 20px 40px rgba(63, 68, 90, 0.1)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '90%',
        border: `3px solid ${theme.COLORS.tertiary_ligthindigo}`
      }}>
        {/* Quotey Robot Logo */}
        <div style={{
          marginBottom: '2rem',
          position: 'relative'
        }}>
          <img
            src={quoteyBot}
            alt="Quotey Bot"
            style={{
              width: '80px',
              height: '80px',
              animation: hasError ? 'none' : 'pulse 2s infinite',
              filter: hasError ? 'grayscale(50%)' : 'none'
            }}
          />
          {!hasError && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }}>
              <Spinner
                animation="border"
                size="lg"
                style={{
                  color: theme.COLORS.secondary_skyblue,
                  width: '100px',
                  height: '100px',
                  borderWidth: '3px'
                }}
              />
            </div>
          )}
        </div>

        {/* Main Status Message */}
        <h2 style={{
          color: hasError ? theme.COLORS.toast_red : theme.COLORS.primary_navyblue,
          fontSize: '1.5rem',
          fontWeight: '600',
          marginBottom: '1rem',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          {hasError ? (translations[languagePreference]?.authentication?.authenticationFailed || 'Authentication Failed') : getAuthMessage()}
        </h2>

        {/* Sub Message */}
        <p style={{
          color: theme.COLORS.grey_font_header,
          fontSize: '1rem',
          marginBottom: '2rem',
          lineHeight: '1.5'
        }}>
          {getSubMessage()}
        </p>

        {/* Progress Dots */}
        {!hasError && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            {['authenticating', 'verifyingCredentials', 'settingUpSession', 'loadingWorkspace'].map((step, index) => (
              <div
                key={step}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: authStep === step ||
                    ['authenticating', 'verifyingCredentials', 'settingUpSession', 'loadingWorkspace'].indexOf(authStep) > index
                    ? theme.COLORS.secondary_skyblue
                    : theme.COLORS.tertiary_ligthindigo,
                  transition: 'all 0.3s ease',
                  animation: authStep === step ? 'pulse 1s infinite' : 'none'
                }}
              />
            ))}
          </div>
        )}

        {/* Error Message or Redirect Notice */}
        {hasError && (
          <p style={{
            color: theme.COLORS.toast_red,
            fontSize: '0.9rem',
            fontStyle: 'italic'
          }}>
            {languagePreference === 'fr' ? 'Redirection vers la page de connexion...' : 'Redirecting to login page...'}
          </p>
        )}
      </div>

      {/* Add CSS animations to document head */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};